import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import SensitiveRevealToggle from 'garaje/components/entries/sensitive-reveal-toggle';
import SensitiveFieldShield from 'garaje/components/sensitive-field-shield';
import isRemovedString from 'garaje/helpers/is-removed-string';
import removedString from 'garaje/helpers/removed-string';
import italicizeIfRemoved from 'garaje/modifiers/italicize-if-removed';
import { isSensitiveFieldMasked, type SensitiveState } from 'garaje/services/sensitive-fields-visibility';

interface EntriesPrivateNotesFieldSignature {
  Args: {
    value?: string | null;
    update: (value: string) => void;
    sensitiveState?: SensitiveState;
    disabled?: boolean;
    id?: string;
  };
  Element: HTMLDivElement;
}

/**
 * The visitor "Private notes" textarea with the sensitive-field treatment: a
 * shield on the label when the category is sensitive, the value masked as
 * "Hidden" until revealed, and the eyeball reveal toggle for viewers allowed to
 * see it. The textarea analog of {@link EntriesInputField}, driven by the
 * `sensitive_fields["Private notes"]` annotation the backend emits. The masked
 * value is displayed only — the real value is never clobbered — so revealing
 * shows it intact.
 */
export default class EntriesPrivateNotesField extends Component<EntriesPrivateNotesFieldSignature> {
  @tracked isSensitiveRevealed = false;

  get id(): string {
    return this.args.id ?? 'private-notes';
  }

  get isSensitive(): boolean {
    return Boolean(this.args.sensitiveState);
  }

  get isSensitiveRevealable(): boolean {
    return this.args.sensitiveState === 'visible';
  }

  get isSensitiveMasked(): boolean {
    return isSensitiveFieldMasked(this.args.sensitiveState, this.isSensitiveRevealed);
  }

  toggleSensitiveRevealed = (): void => {
    this.isSensitiveRevealed = !this.isSensitiveRevealed;
  };

  get displayValue(): string | null | undefined {
    if (this.isSensitiveMasked) {
      return 'Hidden';
    }

    if (isRemovedString(this.args.value)) {
      return removedString(this.args.value);
    }

    return this.args.value;
  }

  get isDisabled(): boolean {
    return this.args.disabled || this.isSensitiveMasked || isRemovedString(this.args.value);
  }

  get ariaLabel(): string | undefined {
    // Carry over the {{handle-removed-string}} behaviour this component replaced:
    // announce a deleted value to screen readers. Suppressed while masked, since the
    // field then shows "Hidden" and must not leak that the value was removed.
    if (this.isSensitiveMasked) {
      return undefined;
    }

    return isRemovedString(this.args.value) ? 'Field value was deleted' : undefined;
  }

  <template>
    <div ...attributes>
      <label for={{this.id}}>
        Private notes
        {{#if this.isSensitive}}
          <SensitiveFieldShield />
        {{/if}}
      </label>
      <div class={{if this.isSensitiveRevealable "relative"}}>
        <textarea
          data-private
          data-test-private-notes
          id={{this.id}}
          value={{this.displayValue}}
          aria-label={{this.ariaLabel}}
          disabled={{this.isDisabled}}
          class="resize-y
            {{if this.isSensitiveMasked 'italic !text-carbon-50'}}
            {{if this.isSensitiveRevealable 'pr-8'}}"
          {{on "input" (pick "target.value" @update)}}
          {{italicizeIfRemoved @value}}
        ></textarea>
        {{#if this.isSensitiveRevealable}}
          <SensitiveRevealToggle
            @revealed={{this.isSensitiveRevealed}}
            @onToggle={{this.toggleSensitiveRevealed}}
            class="absolute top-2 right-2"
          />
        {{/if}}
      </div>
    </div>
  </template>
}
